// src/components/BlocCatalog.jsx
import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import {
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Card,
  Empty,
  Input,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";
import BlocDetailsModal from "./BlocDetailsModal";

const isMinioImageKey = (value) =>
  typeof value === "string" && value.startsWith("images/");

const normalizeLegacyUrl = (url) => {
  if (!url) return null;

  if (url.startsWith("http")) {
    return url;
  }

  return `${Constants.SERVER_URL.replace(/\/$/, "")}/${url.replace(
    /^\/+/,
    ""
  )}`;
};

export default function BlocCatalog({ onOpenDetails, onAddQuick }) {
  const { t, i18n } = useTranslation("BlocCatalog", { useSuspense: false });
  const token = localStorage.getItem("token");

  const headers = useMemo(
    () => ({
      Authorization: "Bearer " + token,
      "Accept-Language": i18n.language,
    }),
    [token, i18n.language]
  );

  const dragType = "application/x-bloc";

  const [q, setQ] = useState("");
  const [all, setAll] = useState([]);
  const [imageUrls, setImageUrls] = useState({});

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedBlocKey, setSelectedBlocKey] = useState(null);

  const getSignedImageUrl = async (imageKey) => {
    if (!imageKey) return null;

    if (imageKey.startsWith("http")) {
      return imageKey;
    }

    if (isMinioImageKey(imageKey)) {
      const res = await axios.get(
        `${Constants.SERVER_URL}/api/minio/presign-download`,
        {
          params: {
            bucket: "rxapa-assets",
            key: imageKey,
          },
          headers,
        }
      );

      return res.data.downloadUrl;
    }

    return normalizeLegacyUrl(imageKey);
  };

  useEffect(() => {
    setLoading(true);

    axios
      .get(`${Constants.SERVER_URL}/blocs`, { headers })
      .then((res) => setAll(res.data || []))
      .catch((error) => {
        setErr(error);

        const raw = error?.response?.data?.message || error?.message;
        message.error(raw || "Erreur de chargement des blocs");
      })
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    if (!all.length) {
      setImageUrls({});
      return;
    }

    let cancelled = false;

    async function loadImages() {
      const entries = await Promise.all(
        all.map(async (bloc) => {
          if (!bloc.imageUrl) {
            return [bloc.id, null];
          }

          try {
            const signedUrl = await getSignedImageUrl(bloc.imageUrl);
            return [bloc.id, signedUrl];
          } catch {
            return [bloc.id, null];
          }
        })
      );

      if (!cancelled) {
        setImageUrls(Object.fromEntries(entries));
      }
    }

    loadImages();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();

    return all.filter((bloc) => {
      const name = String(bloc?.name || "").toLowerCase();
      const description = String(bloc?.description || "").toLowerCase();

      return !term || name.includes(term) || description.includes(term);
    });
  }, [all, q]);

  function onDragStartBloc(event, bloc) {
    const payload = JSON.stringify({
      id: bloc.id ?? bloc.blocId ?? null,
      key: bloc.key ?? bloc.blocKey ?? null,
      name: bloc.name ?? "",
    });

    try {
      event.dataTransfer.setData(dragType, payload);
    } catch {
      event.dataTransfer.setData("text/plain", payload);
    }

    event.dataTransfer.effectAllowed = "copy";
  }

  function handleOpenDetails(bloc) {
    const key = bloc?.key || bloc?.id;

    setSelectedBlocKey(key);
    setDetailsOpen(true);

    onOpenDetails?.(bloc);
  }

  return (
    <>
      <aside style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Card size="small">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={
              t("label_search_blocs") || "Rechercher par nom ou description"
            }
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
        </Card>

        <div style={{ minHeight: 160 }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <Spin tip={t("label_loading") || "Chargement du catalogue…"} />
            </div>
          ) : err ? (
            <Empty
              description={
                t("label_error_loading") ||
                "Erreur lors du chargement des blocs."
              }
            />
          ) : filtered.length === 0 ? (
            <Empty description={t("label_no_blocs") || "Aucun bloc trouvé."} />
          ) : (
            <div
              style={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              }}
            >
              {filtered.map((bloc) => (
                <Card
                  key={bloc.id}
                  size="small"
                  hoverable
                  draggable
                  onDragStart={(event) => onDragStartBloc(event, bloc)}
                  cover={
                    imageUrls[bloc.id] ? (
                      <img
                        loading="lazy"
                        src={imageUrls[bloc.id]}
                        alt={bloc.name}
                        style={{
                          height: 120,
                          objectFit: "cover",
                        }}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null
                  }
                  actions={[
                    <Tooltip
                      title={t("label_details") || "Détails"}
                      key="details"
                    >
                      <InfoCircleOutlined
                        onClick={() => handleOpenDetails(bloc)}
                      />
                    </Tooltip>,

                    <Tooltip
                      title={
                        t("label_add_to_session") || "Ajouter à la séance"
                      }
                      key="add"
                    >
                      <PlusOutlined
                        onClick={() =>
                          onAddQuick ? onAddQuick(bloc) : handleOpenDetails(bloc)
                        }
                      />
                    </Tooltip>,
                  ]}
                >
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Typography.Text strong ellipsis title={bloc.name}>
                      {bloc.name}
                    </Typography.Text>

                    <Typography.Paragraph ellipsis={{ rows: 2 }}>
                      {bloc.description || "—"}
                    </Typography.Paragraph>
                  </Space>
                </Card>
              ))}
            </div>
          )}
        </div>
      </aside>

      <BlocDetailsModal
        open={detailsOpen}
        blocKey={selectedBlocKey}
        onClose={() => setDetailsOpen(false)}
      />
    </>
  );
}